// PurchaseOrderItemsInlineCells.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Utilities
import {
  formatCurrencyDollarsString,
  formatPrice,
  isCurrencyInputPartial,
  parseCurrencyInputToCents,
} from 'utilities/Currency';

function cleanString(value) {
  return String(value || '').trim();
}

/**
 * Input size that grows with content (with a sensible floor).
 *
 * @param {string} value
 * @param {string} [placeholder]
 * @param {number} [min=4]
 * @returns {number}
 */
function inputSize(value, placeholder = '', min = 4) {
  const length = Math.max(cleanString(value).length, cleanString(placeholder).length, min);
  return Math.min(length + 1, 48);
}

/**
 * Read-only fallback for a table cell.
 *
 * @param {string} [value]
 * @param {string} [placeholder='—']
 * @returns {ReactNode}
 */
function CellPlain({ value, placeholder = '—' }) {
  const text = cleanString(value);
  if (!text) {
    return <span className="purchase-order-items-muted">{placeholder}</span>;
  }
  return text;
}

/**
 * Shared commit helpers for blur / Enter / Escape.
 *
 * @param {object} options
 * @returns {{ handleBlur: Function, handleKeyDown: Function }}
 */
function buildInlineCommitHandlers({ draft, setDraft, committed, onCommit, parseCommit }) {
  const handleBlur = () => {
    const result = parseCommit(draft);
    if (result.revert) {
      setDraft(committed);
      return;
    }
    if (result.unchanged) {
      setDraft(committed);
      return;
    }
    if (typeof onCommit === 'function') onCommit(result.value);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.currentTarget.blur();
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      setDraft(committed);
      event.currentTarget.blur();
    }
  };

  return { handleBlur, handleKeyDown };
}

/**
 * Compact text cell with save-on-blur.
 *
 * @param {object} props
 * @returns {ReactNode}
 */
export const InlineTextCell = React.memo(({
  value = '',
  placeholder = '',
  canEdit = false,
  emphasized = false,
  maxWidth = null,
  onCommit = null,
}) => {
  const committed = String(value || '');
  const [draft, setDraft] = useState(committed);
  const maxWidthStyle = maxWidth != null
    ? {
      maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth,
    }
    : undefined;

  useEffect(() => {
    setDraft(committed);
  }, [committed]);

  const { handleBlur, handleKeyDown } = buildInlineCommitHandlers({
    draft,
    setDraft,
    committed,
    onCommit,
    parseCommit: (nextDraft) => {
      const next = cleanString(nextDraft);
      const prev = cleanString(committed);
      if (next === prev) return { unchanged: true };
      return { value: next };
    },
  });

  if (!canEdit) {
    return (
      <span
        className={[
          emphasized ? 'purchase-order-items-item-title' : '',
          maxWidth != null ? 'purchase-order-items-inline-clamp' : '',
        ].filter(Boolean).join(' ') || undefined}
        style={maxWidthStyle}
      >
        <CellPlain value={committed} placeholder={placeholder || '—'} />
      </span>
    );
  }

  return (
    <input
      className={[
        'purchase-order-items-inline-input',
        emphasized ? 'is-emphasized' : '',
      ].filter(Boolean).join(' ')}
      style={maxWidthStyle}
      type="text"
      size={inputSize(draft, placeholder, emphasized ? 8 : 4)}
      value={draft}
      placeholder={placeholder}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    />
  );
});

InlineTextCell.displayName = 'InlineTextCell';

/**
 * Compact number cell with save-on-blur.
 *
 * @param {object} props
 * @returns {ReactNode}
 */
export const InlineNumberCell = React.memo(({
  value = null,
  placeholder = '0',
  canEdit = false,
  onCommit = null,
}) => {
  const committed = value == null || value === '' ? '' : String(value);
  const [draft, setDraft] = useState(committed);

  useEffect(() => {
    setDraft(committed);
  }, [committed]);

  const { handleBlur, handleKeyDown } = buildInlineCommitHandlers({
    draft,
    setDraft,
    committed,
    onCommit,
    parseCommit: (nextDraft) => {
      const next = cleanString(nextDraft);
      if (next === cleanString(committed)) return { unchanged: true };
      if (next === '') return { value: null };
      const number = Number(next);
      if (!Number.isFinite(number)) return { revert: true };
      return { value: number };
    },
  });

  if (!canEdit) {
    return <CellPlain value={committed} />;
  }

  return (
    <input
      className="purchase-order-items-inline-input is-numeric"
      type="text"
      inputMode="decimal"
      size={inputSize(draft, placeholder, 3)}
      value={draft}
      placeholder={placeholder}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    />
  );
});

InlineNumberCell.displayName = 'InlineNumberCell';

/**
 * Compact money cell (major-unit draft, cents on commit).
 *
 * @param {object} props
 * @returns {ReactNode}
 */
export const InlineMoneyCell = React.memo(({
  valueCents = null,
  placeholder = '0.00',
  canEdit = false,
  onCommit = null,
}) => {
  const committed = (
    valueCents === null || valueCents === undefined || valueCents === ''
      ? ''
      : formatCurrencyDollarsString(valueCents)
  );
  const [draft, setDraft] = useState(committed);

  useEffect(() => {
    setDraft(committed);
  }, [committed]);

  const { handleBlur, handleKeyDown } = buildInlineCommitHandlers({
    draft,
    setDraft,
    committed,
    onCommit,
    parseCommit: (nextDraft) => {
      const next = cleanString(nextDraft);
      if (next === '') {
        if (committed === '') return { unchanged: true };
        return { value: null };
      }
      const parsed = parseCurrencyInputToCents(next);
      if (!parsed.ok) return { revert: true };
      const nextCents = parsed.cents === '' ? null : parsed.cents;
      const prevCents = (
        valueCents === null || valueCents === undefined || valueCents === ''
          ? null
          : Number(valueCents)
      );
      if (nextCents === prevCents) return { unchanged: true };
      return { value: nextCents };
    },
  });

  if (!canEdit) {
    if (valueCents === null || valueCents === undefined || valueCents === '') {
      return <span className="purchase-order-items-muted">—</span>;
    }
    return formatPrice(valueCents) || '—';
  }

  return (
    <input
      className="purchase-order-items-inline-input is-numeric"
      type="text"
      inputMode="decimal"
      size={inputSize(draft, placeholder, 5)}
      value={draft}
      placeholder={placeholder}
      onChange={(event) => {
        const next = event.target.value;
        if (next === '' || isCurrencyInputPartial(next)) {
          setDraft(next);
        }
      }}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    />
  );
});

InlineMoneyCell.displayName = 'InlineMoneyCell';

/**
 * Compact native select cell (sizes to selected label).
 *
 * @param {object} props
 * @returns {ReactNode}
 */
export const InlineSelectCell = React.memo(({
  value = '',
  options = [],
  placeholder = 'Add…',
  canEdit = false,
  onCommit = null,
}) => {
  const selected = String(value || '');
  const normalizedOptions = useMemo(
    () => (Array.isArray(options) ? options : []),
    [options],
  );
  const label = (
    normalizedOptions.find((option) => String(option?.value) === selected)?.label
  ) || selected;

  if (!canEdit) {
    return <CellPlain value={label} />;
  }

  return (
    <select
      className="purchase-order-items-inline-select"
      value={selected}
      onChange={(event) => {
        const next = String(event.target.value || '').trim();
        if (next === selected) return;
        if (typeof onCommit === 'function') onCommit(next);
      }}
    >
      <option value="">{placeholder}</option>
      {normalizedOptions.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
});

InlineSelectCell.displayName = 'InlineSelectCell';
